import React from 'react'
import { FlowGrid, List, Skeleton } from '@fs/zion-ui'
import PersonDisplayV2 from '../../../views/indexing/components/selectPersonV2/PersonDisplayV2'
import { useR2StateContext } from '../../../providers/r2Provider/R2Context'

/**
 * Renders a list of persons, grouped by records, and floating persons.
 * Meant to replace a legacy NamesView component.
 *
 * NOTE: This component is designed to be used in conjunction with the `usePersonList` hook
 * and a filter component (e.g., `FilterInput`) to manage state and filtering logic.
 *
 * @param {Object} props - The component props
 * @param {Array<{record: Object, persons: Array<Object>}>} [props.filteredRecordsWithPersons=[]] - Array of objects containing records and their associated persons
 * @param {Array<Object>} [props.filteredFloatingPersons=[]] - Array of persons not attached to records
 * @param {Function} props.onPersonSelect - Callback function when a person is selected
 * @param {boolean} [props.showLoading=false] - Boolean to show loading skeletons
 * @param {boolean} [props.isJiapu=false] - Boolean indicating if it's a Jiapu record (hides list if true)
 * @param {JSX.Element|null} [props.topChildListItem=null] - Optional JSX element to render at the top of the list (used for an addPerson list item / button)
 * @returns {JSX.Element} The rendered component
 */
export default function PersonsList({
  filteredRecordsWithPersons = [],
  filteredFloatingPersons = [],
  onPersonSelect,
  showLoading = false,
  isJiapu = false,
  topChildListItem = null,
}) {
  const { highlightedPersonId } = useR2StateContext()

  // Early return loading skeletons if data is still loading
  if (showLoading) {
    return (
      <List>
        <Skeleton.ListItem hasSecondLine />
        <Skeleton.ListItem hasSecondLine />
        <Skeleton.ListItem hasSecondLine />
        <Skeleton.ListItem hasSecondLine />
        <Skeleton.ListItem hasSecondLine />
        <Skeleton.ListItem hasSecondLine />
      </List>
    )
  }

  // Filter out records with no persons to avoid rendering empty lists
  const cleanedRecordsWithPersons = filteredRecordsWithPersons.filter(({ persons }) => persons && persons.length > 0)

  return (
    <>
      {/* Person List */}
      {!isJiapu && (
        // The asList prop makes FlowGrid render as a <ul> for accessibility and semantic purposes, bordersX adds borders between items
        <FlowGrid orientation="vertical" columnCount={1} guttersY="md" asList bordersX>
          {/* Top Child List Item (e.g., Add Name button) */}
          {topChildListItem !== null && topChildListItem}

          {/* List of Records with Persons */}
          {cleanedRecordsWithPersons.map(({ record, persons }) => (
            <List key={record?.id}>
              {persons?.map((person) => (
                <PersonDisplayV2
                  person={person}
                  key={person?.id}
                  record={record}
                  isSelected={highlightedPersonId === person?.id}
                  onPersonSelect={onPersonSelect}
                />
              ))}
            </List>
          ))}

          {/* Floating Persons (not attached to records) */}
          {filteredFloatingPersons.length > 0 && (
            <List>
              {filteredFloatingPersons
                .toSorted((a, b) => (a.primary > b.primary ? -1 : 1))
                .map((person) => (
                  <PersonDisplayV2
                    person={person}
                    key={person?.id}
                    isSelected={highlightedPersonId === person?.id}
                    onPersonSelect={onPersonSelect}
                  />
                ))}
            </List>
          )}
        </FlowGrid>
      )}
    </>
  )
}
